import { useSyncExternalStore } from 'react';
import {
  emptyMetaCache,
  metaCachePut,
  readMetaCache,
  touchMetaCache,
  CHANNEL_META_VERSION,
  type CachedChannelMeta,
  type ChannelMetaCache,
} from '@visious/core';

/**
 * R14's storage half — the twenty lines core is not allowed to contain.
 *
 * `cache/metaCache.ts` owns the policy (LRU on last use, a hard entry cap, a staleness TTL, wholesale
 * eviction on a version change) as pure functions over a value. This owns exactly three things core
 * cannot: where the value lives, how it is serialised, and telling React it changed.
 *
 * **The key prefix is deliberate.** Not `visious.token.*`, because R9's `clearOtherInstances()` deletes
 * every key under that prefix that is not the current instance's on every boot (docs/specs/auth.md
 * records the reload that ate a backup parked there). And **not keyed per instance either**, which is
 * the interesting half: what is cached is `/ggpht/…` *paths*, which every instance proxies, so the
 * cache is genuinely portable and re-keying it per origin would throw away a working avatar every time
 * the instance changed. That portability is the whole reason `instanceAssetPath` exists.
 *
 * There is nothing account-specific in here — a channel's name and avatar are public — so it is not
 * evicted on sign-out, unlike the query cache (`AUTH_QUERY_KEY`). Signing out must not tell you
 * anything about who was signed in, and this holds nothing that would.
 */

const STORAGE_KEY = 'visious.channelMeta';

/** Read once and kept in memory: this is on the render path and `localStorage` reads are synchronous. */
let cache: ChannelMetaCache = load();
const listeners = new Set<() => void>();

function load(): ChannelMetaCache {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) return emptyMetaCache<CachedChannelMeta>(CHANNEL_META_VERSION);
    // R4's division of labour: `JSON.parse` happens out here, core validates the parsed value.
    return readMetaCache<CachedChannelMeta>(
      JSON.parse(raw) as ChannelMetaCache | null,
      CHANNEL_META_VERSION,
    );
  } catch {
    // A blocked storage context, a quota error, or a blob some other version wrote. All three mean
    // "start empty", which for a cache costs one round of requests and nothing else.
    return emptyMetaCache<CachedChannelMeta>(CHANNEL_META_VERSION);
  }
}

function persist(next: ChannelMetaCache): void {
  cache = next;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Swallowed on purpose, and the in-memory value is kept: a cache that does not survive a reload is
    // still a cache for the rest of this session. Same judgement `authStorage.write` makes, with much
    // lower stakes — nothing here is a credential.
  }
  for (const listener of listeners) listener();
}

export function putChannelMeta(metas: readonly CachedChannelMeta[]): void {
  const next = metaCachePut(cache, metas);
  if (next !== cache) persist(next);
}

/** Record that these rows were rendered from the cache, which is what keeps the LRU order meaningful. */
export function touchChannelMeta(ids: readonly string[], nowEpochSec: number): void {
  const next = touchMetaCache(cache, ids, nowEpochSec);
  // `touchMetaCache` returns the same object when nothing moved, so a list re-rendering in the same
  // second writes nothing — which matters, because this runs on every render of every subscription row.
  if (next !== cache) persist(next);
}

/** How many rows are held — the number the settings page puts next to "clear" (3e). */
export function channelMetaCount(): number {
  return cache.entries.length;
}

/** Throw the cache away — §8's Settings page. Costs one round of avatar fetches and nothing else. */
export function clearChannelMeta(): void {
  persist(emptyMetaCache<CachedChannelMeta>(CHANNEL_META_VERSION));
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

function getSnapshot(): ChannelMetaCache {
  return cache;
}

export function useChannelMetaCache(): ChannelMetaCache {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
